/* eslint-disable react/jsx-no-bind */
import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core'
import xhr from 'xhr'
import { useCookies } from 'react-cookie'

import { Auth0Provider } from './react-auth0-spa'
import config from './config'
import AuthWrapper from './AuthWrapper'
import Loader from './common/components/notifications/Loader'
import LoginError from './LoginError'

const useStyles = makeStyles(() => ({
  devHostInput: {
    width: '70%',
    maxWidth: '30rem',
  },
  loading: { display: 'flex', justifyContent: 'center', paddingTop: '5%' },
}))

// A function that routes the user to the right place
// after login
const onRedirectCallback = appState => {
    const sendTo = appState && appState.targetUrl ? appState.targetUrl : window.location.pathname
    window.location.replace(sendTo)
}

const getClientInfo = (hostname, setClientInfo, onError) => {
    const handleError = err => {
        onError(err)
        return
    }
    xhr({
        method: 'post',
        body: JSON.stringify({
            ccURI: hostname
        }),
        uri: 'https://crud.v3.invintusmedia.com/Accounts/verifyEntryDom',
        headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "Authorization": config.apiAuthentication.vendorName,
            "Wsc-api-key": config.apiAuthentication.vendorKey
        }
    }, (err, resp) => {
        // Every branch below must RETURN after reporting. Previously
        // `handleError` only returned from itself, so `setClientInfo` ran
        // even on a failure and handed downstream code a bag with no
        // clientID -- which is what surfaced later as a crash rather than
        // as the error it actually was.
        if (err) {
            handleError(err)
            return
        }
        if (!resp) {
            handleError('No response from the account service')
            return
        }
        // A non-200 (commonly 403 for a hostname that is not a registered
        // entry domain -- localhost, a branch preview) used to fall
        // through silently, leaving the page on "Loading" forever with
        // nothing logged and no way back.
        if (resp.statusCode !== 200) {
            handleError(
                'Account lookup for "' + hostname + '" failed (' + resp.statusCode + '). ' +
                'This hostname is probably not a registered entry domain.'
            )
            return
        }

        let body
        try {
            body = JSON.parse(resp.body)
        } catch (parseErr) {
            handleError(parseErr)
            return
        }

        // check for errors and correct data
        if (body.errors && body.errors.message) {
            handleError(body.errors.message)
            return
        }
        if (!body.data) {
            handleError('no response')
            return
        }
        if (!body.data.clientID) {
            handleError('No client id found for "' + hostname + '"')
            return
        }
        if (!body.data.appID) {
            handleError('No app id found for "' + hostname + '"')
            return
        }

        // set client info
        setClientInfo(body.data)
    })
}

const isEnterpriseClientUrl = url => {
    return url !== 'controlcenter.invintusmedia.com' && url !== 'controlcenter-stage.invintusmedia.com' && url !== 'controlcenter-preview.invintusmedia.com' && !url.endsWith('--control-center.netlify.app')
}

const AuthSelector = props => {
    const { args } = props
    // client request state
    const [clientInfo, setClientInfo] = useState(null)
    const [sentRequest, setSentRequest] = useState(false)
    const [error, setError] = useState(null)
    const [hostname, setHostName] = useState(process.env.NODE_ENV === 'development' ? 'controlcenter.invintusmedia.com' : window.location.hostname)
    const [devHost, setDevHost] = useState(false)
    const [isEnterpriseClient, setIsEnterpriseClient] = useState(isEnterpriseClientUrl(process.env.NODE_ENV === 'development' ? 'controlcenter.invintusmedia.com' : window.location.hostname))
    const [cookies, setCookies] = useCookies(['loginHostname'])

    const classes = useStyles()

    useEffect(() => {
        const enterprise = isEnterpriseClientUrl(hostname)
        if (!enterprise && isEnterpriseClient) {
            setIsEnterpriseClient(false)
        } else if (enterprise && !isEnterpriseClient) {
            setIsEnterpriseClient(true)
        }
    }, [isEnterpriseClient, hostname])
    if (process.env.NODE_ENV === 'development' && !devHost && cookies.loginHostname) {
    // page was redirected after a login set the hostname and try login again, if this is the dev env
        if (cookies.loginHostname !== window.location.hostname && hostname !== cookies.loginHostname) {
            setHostName(cookies.loginHostname)
            setDevHost(true)
        }
    }

    const handleAuth0Error = (err) => {
        // if auth0 is down we can show an error 
        // this call will fail if you block auth.invintusmedia.com requests
        setError(err)
    }

    // allow setting hostname in dev environment
    if (process.env.NODE_ENV === 'development' && !devHost && !cookies.loginHostname) {
        return <div>
            <span>Development hostname: </span>
            <input
                className={classes.devHostInput}
                onChange={e => {
                    setHostName(e.target.value)
                }}
                defaultValue={hostname}
            />
            <button
                onClick={() => {
                    setDevHost(true)
                    // set a cookie to hold the login info and attempt login with redirect results that lasts 30 seconds
                    setCookies('loginHostname', hostname, { path: '/', maxAge: 600 })
                }}
            >set</button>
            <div>
        Using Enterprise login? {' ' + isEnterpriseClient}
            </div>
      You will be locked into the chosen hostname for 10 minute to handle login redirects<br/>
      Delete your cookies to change the hostname in the meantime
        </div>
    }

    const ErrorView = error ? <LoginError /> : null
    if (error) {
        console.error('error:', error)
    }
    if (isEnterpriseClient) {
        if (sentRequest) {
            if (!clientInfo) {
                return <>
          <div className={classes.loading}>
              <div>
                  Loading
              </div>
              {ErrorView}
          </div>
          <Loader />
      </>
            } else if (clientInfo) {
                // client info loaded
                return <><Auth0Provider
                    domain={config.Auth0.auth0_domain}
                    client_id={clientInfo.appID}
                    redirect_uri={window.location.origin}
                    onRedirectCallback={onRedirectCallback}
                    scope="openid profile email"
                    useRefreshTokens={true}
                    cacheLocation='localstorage'
                    onError={handleAuth0Error}
                >
                    <AuthWrapper
                        args={args}
                        clientID={clientInfo.clientID}
                        type="Enterprise"
                    />
                </Auth0Provider>
                {ErrorView}
                </>
            }
        } else {
            // send request to get enterprise client info
            getClientInfo(hostname, setClientInfo, err => setError(err))
            setSentRequest(true)
        }
    } else {
        return <><Auth0Provider
            domain={config.Auth0.auth0_domain}
            client_id={config.Auth0.auth0_client_v2}
            redirect_uri={window.location.origin}
            onRedirectCallback={onRedirectCallback}
            scope="openid profile email"
            useRefreshTokens={true}
            cacheLocation="localstorage"
            onError={handleAuth0Error}
        >
            <AuthWrapper
                args={args}
                type="Auth0"
            />
        </Auth0Provider>
        {ErrorView}
        </>
    }
}

export default AuthSelector
